import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { portfolioPhotos } from "@/assets/performance/photos";

const clients = [
  {
    name: "South East Dental / Heartland Dental",
    image: portfolioPhotos.southEastDental,
    category: "Dental Group",
    testimonial:
      "He is truly the best commercial photographer in Florida, capturing every detail of our practice with professionalism and creativity. Someone who delivers high-quality content that drives visibility and engagement. Highly recommended!",
    results: ["Enterprise-level content", "Multi-location branding", "Professional storytelling"],
  },
  {
    name: "Dental Designs",
    image: portfolioPhotos.dentalDesigns,
    category: "Dental Practice",
    testimonial:
      "Nathaniel captured every detail of our practice with professionalism and creativity. His ability to combine storytelling with marketing strategy makes him not just a photographer, but a partner in helping us grow our brand.",
    results: ["Full brand photography", "Practice visibility boost", "Content-driven patient acquisition"],
  },
  {
    name: "2R Cooling & Refrigeration",
    image: portfolioPhotos.cooling,
    category: "HVAC Services",
    testimonial:
      "Working with Nathaniel Santiago at N. Santiago Media has been a game-changer for us. His marketing and content strategies helped us grow our business quickly, with a 60% revenue increase, allowing us to expand throughout East Florida.",
    results: ["60% revenue increase", "Expanded throughout East Florida", "Brand identity & content strategy"],
  },
];

const ResultsSection = () => {
  return (
    <section id="results" className="section-padding section-light overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-primary font-semibold tracking-[0.2em] uppercase text-sm mb-4"
          >
            Testimonials
          </motion.p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-4 text-graphite">Client Growth Stories</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Real feedback from real businesses we've helped grow.
          </p>
        </motion.div>

        <div className="space-y-12">
          {clients.map((client, i) => (
            <motion.div
              key={client.name}
              initial={{ opacity: 0, x: i % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className={`flex flex-col ${i % 2 === 1 ? "md:flex-row-reverse" : "md:flex-row"} gap-8 items-stretch`}
            >
              {/* Image */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className="md:w-1/2 relative overflow-hidden rounded-2xl min-h-[280px] md:min-h-[360px]"
              >
                {client.image ? (
                  <img
                    src={client.image.src}
                    srcSet={client.image.srcSet}
                    sizes="(min-width: 1280px) 560px, (min-width: 768px) 45vw, calc(100vw - 48px)"
                    width={1440}
                    height={960}
                    decoding="async"
                    alt={`${client.name} — N. Santiago Media client`}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-gold-light/20 flex items-center justify-center">
                    <span className="font-heading text-2xl md:text-3xl text-graphite/60 text-center px-8">
                      {client.name}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="bg-primary/90 text-primary-foreground text-xs font-semibold tracking-wider uppercase px-3 py-1.5 rounded-full">
                    {client.category}
                  </span>
                </div>
              </motion.div>

              {/* Content */}
              <div className="md:w-1/2 flex flex-col justify-center">
                <motion.h3
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="font-heading text-2xl md:text-3xl mb-4 text-graphite"
                >
                  {client.name}
                </motion.h3>

                {/* Testimonial */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="relative mb-6"
                >
                  <Quote className="w-8 h-8 text-primary/30 mb-2" />
                  <p className="text-graphite/80 italic leading-relaxed text-sm md:text-base">
                    "{client.testimonial}"
                  </p>
                </motion.div>

                {/* Results */}
                <ul className="space-y-2">
                  {client.results.map((r, ri) => (
                    <motion.li
                      key={r}
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.4 + ri * 0.1 }}
                      className="flex items-center gap-3 text-sm text-graphite"
                    >
                      <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                      {r}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ResultsSection;
